import React, { useContext, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import DataTable from 'components/utils/DataTable';
import styles from './styles.module.css';
import Info from 'components/utils/Alert/Info';
import { Grid, Tooltip, CircularProgress, IconButton } from '@mui/material';
import DateTimeHelpers from 'helpers/DateTimeHelpers';
import { STORAGE_KEYS, BACKEND_URL, INVOICE_LAYOUTS, PRINT_TYPES } from 'const';
import ShopsHelper from 'helpers/ShopsHelper';
import { ShopContext } from 'Context/ShopContext';
import { BillFormatContext } from 'Context/BillFormatContext';
import { TerminalContext } from 'Context/TerminalContext';
import { PAPER_SIZE } from 'const';
import PrintDocument from 'components/utils/PrintDocument/index';
import SaleService from 'services/SaleService';
import TerminalServices from 'services/TerminalServices';

function CustomerSaleDialog(props) {
  const { t: tCommon } = useTranslation('common');
  const { t: tPopup } = useTranslation('popup');
  const { shop } = useContext(ShopContext);
  const { getBillFormat } = useContext(BillFormatContext);
  const { selectedTerminal } = useContext(TerminalContext);
  const { toggleItemsTab, rowData } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [saleToPrint, setSaleToPrint] = useState(null);
  const [billFormatToPrint, setBillFormatToPrint] = useState(null);
  const [totalsToPrint, setTotalsToPrint] = useState(null);
  const [terminalToPrint, setTerminalToPrint] = useState(null);
  const [paperSize, setPaperSize] = useState(PAPER_SIZE.A4);
  const items = rowData?.items || [];
  const columnHeader = [
    {
      id: 'item.name',
      label: tCommon('Name'),
      type: 'text',
    },
    {
      id: 'quantity',
      label: tCommon('Quantity'),
      type: 'floatQuantity',
    },
    {
      id: 'rate',
      label: tCommon('Price'),
      type: 'floatAmount',
    },
    {
      id: 'tax',
      label: tCommon('Tax'),
      type: 'text',
    },
    {
      label: tCommon('Total'),
      id: 'total',
      type: 'callback',
      viewRender: (item) => {
        return ShopsHelper.getAmountFormatted(shop, item.quantity * item.rate + item.tax);
      },
    },
  ];
  const getDate = (time) => {
    return DateTimeHelpers.convertDateToIsoDMY(time);
  };
  const getTime = (time) => {
    const date = time.split('T')[1].split('.')[0];
    return DateTimeHelpers.formatServerTimeSegment(date);
  };

  const prepareInvoiceData = async (billId, existingSale = null) => {
    const fullSale = existingSale && existingSale.items ? existingSale : await SaleService.getSale(billId);

    let parsedTerminalId = fullSale?.terminal?.id || fullSale?.terminal_id || fullSale?.initiated_terminal_id;

    if (!parsedTerminalId) {
      parsedTerminalId = selectedTerminal?.id;
    }

    let billFormat = null;
    if (parsedTerminalId) {
      billFormat = await getBillFormat(parsedTerminalId);
    }

    let terminal = selectedTerminal;
    if (parsedTerminalId) {
      try {
        terminal = await TerminalServices.getTerminal(parsedTerminalId);
      } catch (e) {
        console.error('Failed to fetch terminal details', e);
      }
    }

    const totals = (fullSale.items || []).reduce(
      (acc, item) => {
        acc.payable += item.payable || 0;
        acc.tax += item.tax || 0;
        acc.cess += item.cess || 0;
        return acc;
      },
      { payable: 0, tax: 0, cess: 0 }
    );

    return { fullSale, billFormat, totals, terminal };
  };

  const downloadPdfInvoice = async (bill) => {
    if (!bill) return;

    const web_invoice_format = shop?.web_invoice_format;

    if (
      web_invoice_format === INVOICE_LAYOUTS.A4_BACKEND ||
      web_invoice_format === INVOICE_LAYOUTS.A5_BACKEND ||
      !web_invoice_format
    ) {
      const authToken = localStorage.getItem(STORAGE_KEYS.TOKEN);
      const shopId = localStorage.getItem(STORAGE_KEYS.SHOP_ID);

      // let formatType = '';
      // if (web_invoice_format === INVOICE_LAYOUTS.A4_BACKEND) formatType = INVOICE_LAYOUTS.A4_BACKEND;
      // else if (web_invoice_format === INVOICE_LAYOUTS.A5_BACKEND) formatType =INVOICE_LAYOUTS.A5_BACKEND;

      const url = `${BACKEND_URL}/reports/sales/details/pdf/?token=${authToken}&shop_id=${shopId}&bill_no=${bill.bill_no}`;
      window.open(url);
      return;
    }

    setIsLoading(true);
    try {
      const { fullSale, billFormat, totals, terminal } = await prepareInvoiceData(bill.id, bill);

      let selectedPaperSize = PAPER_SIZE.A4;
      if (web_invoice_format === INVOICE_LAYOUTS.A5_PORTRAIT_FRONTEND) {
        selectedPaperSize = PAPER_SIZE.A5_VERTICAL;
      } else if (web_invoice_format === INVOICE_LAYOUTS.A5_LANDSCAPE_FRONTEND) {
        selectedPaperSize = PAPER_SIZE.A5_HORIZONTAL;
      } else if (web_invoice_format === INVOICE_LAYOUTS.A4_FRONTEND) {
        selectedPaperSize = PAPER_SIZE.A4;
      }

      setPaperSize(selectedPaperSize);
      setSaleToPrint(fullSale);
      setBillFormatToPrint(billFormat);
      setTotalsToPrint(totals);
      setTerminalToPrint(terminal);
    } catch (error) {
      console.error('Failed to prepare frontend print data', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (saleToPrint && totalsToPrint) {
      document.body.classList.add('printing-invoice');
      setTimeout(() => {
        window.print();
        document.body.classList.remove('printing-invoice');
        setSaleToPrint(null);
        setBillFormatToPrint(null);
        setTotalsToPrint(null);
        setTerminalToPrint(null);
      }, 500);
    }
  }, [saleToPrint, billFormatToPrint, totalsToPrint]);

  return (
    <div className={styles.popUp}>
      <div className={styles.contentWrapper}>
        <div className={styles.headSec}>
          <h2 className={styles.editTitle}>{tCommon('Sale Details')}</h2>
          <div>
            <Tooltip title={tCommon('Download invoice')} placement="bottom">
              <div style={{ display: 'inline-block' }}>
                {isLoading ? (
                  <CircularProgress size={20} sx={{ mr: 2 }} />
                ) : (
                  <IconButton sx={{ mr: 2, cursor: 'pointer' }} onClick={() => downloadPdfInvoice(rowData)}>
                    <PrintIcon />
                  </IconButton>
                )}
              </div>
            </Tooltip>
            <Tooltip title={tCommon('Close')} placement="bottom">
              <IconButton onClick={() => toggleItemsTab(null)}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </div>
        </div>
        <div className={styles.changeable} style={{ margin: '10px' }}>
          <Grid container style={{ marginTop: '5px' }} spacing={1}>
            <Grid item xs={6} sm={3}>
              <h5 className={styles.textHead}>{tCommon('Bill No')}</h5>
            </Grid>
            <Grid item xs={6} sm={3}>
              <div style={{ display: 'flex' }}>
                <h5 className={styles.textValues}>{rowData?.bill_no}</h5>
              </div>
            </Grid>
            <Grid item xs={6} sm={3}>
              <h5 className={styles.textHead}>{tCommon('Tax')}</h5>
            </Grid>
            <Grid item xs={6} sm={3}>
              <h5 className={styles.textValues}>{rowData?.tax || 0}</h5>
            </Grid>
          </Grid>

          <Grid container style={{ marginTop: '5px' }} spacing={1}>
            <Grid item xs={6} sm={3}>
              <h5 className={styles.textHead}>{tCommon('Date')}</h5>
            </Grid>
            <Grid item xs={6} sm={3}>
              <h5 className={styles.textValues}>{getDate(rowData?.time)}</h5>
            </Grid>
            <Grid item xs={6} sm={3}>
              <h5 className={styles.textHead}>{tCommon('Additional Charge')}</h5>
            </Grid>
            <Grid item xs={6} sm={3}>
              <h5 className={styles.textValues}>{ShopsHelper.getAmountFormatted(shop, rowData?.cess || 0)}</h5>
            </Grid>
          </Grid>

          <Grid container style={{ marginTop: '5px' }} spacing={1}>
            <Grid item xs={6} sm={3}>
              <h5 className={styles.textHead}>{tCommon('Time')}</h5>
            </Grid>
            <Grid item xs={6} sm={3}>
              <h5 className={styles.textValues}>{getTime(rowData?.time)}</h5>
            </Grid>
            <Grid item xs={6} sm={3}>
              <h5 className={styles.textHead}>{tCommon('Discount')}</h5>
            </Grid>
            <Grid item xs={6} sm={3}>
              <h5 className={styles.textValues}>{ShopsHelper.getAmountFormatted(shop, rowData?.discount || 0)}</h5>
            </Grid>
          </Grid>

          <Grid container style={{ marginTop: '5px' }} spacing={1}>
            <Grid item xs={6} sm={3}>
              <h5 className={styles.textHead}>{tCommon('ID')}</h5>
            </Grid>
            <Grid item xs={6} sm={3}>
              <h5 className={styles.textValues}>{rowData?.id}</h5>
            </Grid>
            <Grid item xs={6} sm={3}>
              <h5 className={styles.textHead}>{tCommon('Payable')}</h5>
            </Grid>
            <Grid item xs={6} sm={3}>
              <h5 className={styles.textValues}>{ShopsHelper.getAmountFormatted(shop, rowData?.payable || '-')}</h5>
            </Grid>
          </Grid>
        </div>
        <Grid container spacing={1} className={styles.subSection} style={{ padding: '5px 15px' }}>
          <Grid item xs={12} className={styles.subHeader}>
            <h2 className={styles.editTitle}>{tCommon('Payment Details')}</h2>
          </Grid>
          {(rowData.sale_payments || []).map((payment) => (
            <>
              <Grid item xs={6} md={3} className={styles.subSectionHeader}>
                <h6 className={styles.textHead}>{payment.mode}</h6>
                <h6 className={styles.textHead}>:</h6>
              </Grid>
              <Grid item xs={6} md={3}>
                <h6 className={styles.textValues}>{payment.amount}</h6>
              </Grid>
            </>
          ))}
        </Grid>
        <div className={styles.tableContent2}>
          {items && items.length ? (
            <DataTable columns={columnHeader} rows={items} />
          ) : (
            <Info title={tPopup('Sale Details not found')} content={tPopup('This bill have no items to list up')} />
          )}
        </div>
      </div>
      {saleToPrint &&
        createPortal(
          <PrintDocument
            type={PRINT_TYPES.SALE}
            data={saleToPrint}
            shop={shop}
            billFormat={billFormatToPrint}
            totals={totalsToPrint}
            paperSize={paperSize}
            terminal={terminalToPrint}
          />,
          document.body
        )}
    </div>
  );
}

export default CustomerSaleDialog;
